'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PrayerRequest {
  id: string;
  name: string;
  message: string;
  isAnonymous: boolean;
  timestamp: string;
}

const PrayerRequestForm = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    message: '',
    isAnonymous: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [prayerRequests, setPrayerRequests] = useState<PrayerRequest[]>([
    {
      id: '1',
      name: 'Sarah Johnson',
      message: 'Praying for a blessed union filled with God\'s love and grace. May your marriage be a testimony of His faithfulness.',
      isAnonymous: false,
      timestamp: '2026-01-20T10:30:00Z',
    },
    {
      id: '2',
      name: 'Anonymous',
      message: 'May the Lord bless your marriage with patience, understanding, and unwavering faith. Congratulations!',
      isAnonymous: true,
      timestamp: '2026-01-21T14:15:00Z',
    },
    {
      id: '3',
      name: 'Pastor Michael',
      message: 'Praying that your home will be built on the solid foundation of Christ. May you grow together in faith and love.',
      isAnonymous: false,
      timestamp: '2026-01-22T09:45:00Z',
    },
  ]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      const newRequest: PrayerRequest = {
        id: Date.now().toString(),
        name: formData.isAnonymous ? 'Anonymous' : formData.name,
        message: formData.message,
        isAnonymous: formData.isAnonymous,
        timestamp: new Date().toISOString(),
      };

      setPrayerRequests(prev => [newRequest, ...prev]);
      setFormData({ name: '', message: '', isAnonymous: false });
      setIsSubmitting(false);
      setSubmitSuccess(true);

      setTimeout(() => setSubmitSuccess(false), 3000);
    }, 1000);
  };

  const formatDate = (timestamp: string) => {
    if (!isHydrated) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!isHydrated) {
    return (
      <div className="space-y-8">
        <div className="bg-card rounded-lg shadow-sacred-md p-8">
          <div className="h-8 bg-surface rounded w-1/3 mb-4 animate-pulse" />
          <div className="h-4 bg-surface rounded w-2/3 mb-6 animate-pulse" />
          <div className="space-y-4">
            <div className="h-12 bg-surface rounded animate-pulse" />
            <div className="h-32 bg-surface rounded animate-pulse" />
            <div className="h-12 bg-surface rounded w-1/2 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Prayer Request Form */}
      <div className="bg-card rounded-lg shadow-sacred-md p-8">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="HeartIcon" size={28} className="text-primary" />
          <h3 className="font-playfair text-2xl font-semibold text-foreground">Submit a Prayer Request</h3>
        </div>
        
        <p className="font-inter text-sm text-muted-foreground mb-6">
          Share your prayers and blessings for Moyak and Gloria as they begin their journey together in faith.
        </p>

        {submitSuccess && (
          <div className="mb-6 p-4 bg-success/10 border border-success rounded-md flex items-center space-x-3">
            <Icon name="CheckCircleIcon" size={20} className="text-success" />
            <p className="font-inter text-sm text-success-foreground">Your prayer has been submitted successfully!</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block font-inter text-sm font-medium text-foreground mb-2">
              Your Name {formData.isAnonymous && <span className="text-muted-foreground">(Optional)</span>}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              disabled={formData.isAnonymous}
              required={!formData.isAnonymous}
              className="w-full px-4 py-3 bg-input border border-border rounded-md font-inter text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Enter your name"
            />
          </div>

          <div>
            <label htmlFor="message" className="block font-inter text-sm font-medium text-foreground mb-2">
              Prayer Message <span className="text-error">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              rows={5}
              className="w-full px-4 py-3 bg-input border border-border rounded-md font-inter text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              placeholder="Share your prayers and blessings..."
            />
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="isAnonymous"
              checked={formData.isAnonymous}
              onChange={handleCheckboxChange}
              className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-ring cursor-pointer"
            />
            <label htmlFor="isAnonymous" className="font-inter text-sm text-foreground cursor-pointer">
              Submit anonymously
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-md font-inter text-sm font-medium hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Icon name="PaperAirplaneIcon" size={18} />
                <span>Submit Prayer</span>
              </>
            )}
          </button>
        </form>
      </div>

      {/* Prayer Wall */}
      <div className="bg-card rounded-lg shadow-sacred-md p-8">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="SparklesIcon" size={28} className="text-secondary" />
          <h3 className="font-playfair text-2xl font-semibold text-foreground">Community Prayer Wall</h3>
        </div>

        <div className="space-y-4">
          {prayerRequests.map((request) => (
            <div key={request.id} className="p-6 bg-surface rounded-lg border border-border">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Icon name="UserCircleIcon" size={20} className="text-primary" />
                  <span className="font-inter text-sm font-medium text-foreground">{request.name}</span>
                </div>
                <span className="font-inter text-xs text-muted-foreground">{formatDate(request.timestamp)}</span>
              </div>
              <p className="font-inter text-sm text-foreground leading-relaxed">{request.message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrayerRequestForm;